import type { VisualizationMode } from '@/types/visualization';

// Split out of EvalOverlayControlBar.tsx (react-refresh/only-export-components)
// — the overlay state model + pure reducers/mappers, not components. Behavior
// unchanged.

// ---------------------------------------------------------------------------
// State model
// ---------------------------------------------------------------------------

export interface EvalOverlayState {
  /** Pose overlay active (true = any mode !== 'off'; pill controls mode on/off) */
  pose: boolean;
  /** Ghost echo mode active (maps to vizConfig.mode === 'ghost_echo') */
  ghost: boolean;
  /** Trajectory trail overlays active on both panels */
  trails: boolean;
  /** Goal annotation overlays active (independent of pose/region overlays) */
  goals: boolean;
  /** Body-region hull overlays active */
  regions: boolean;
  /** 2D / 3D view mode */
  viewMode: '2d' | '3d';
}

/**
 * Default overlay state: **Pose only** (owner ruling 2026-08-03).
 *
 * Every pill used to start ON, so a freshly-opened evaluation stacked the
 * athlete skeleton, a translucent template ghost, hull slabs, goal badges and
 * up to five trajectory trails on one video at once. The owner's call was to
 * "only activate Pose by default (others off)" to clear the clutter.
 *
 * This supersedes the 2026-07-27 ruling that made Trace Pointes (`trails`)
 * on-by-default. The per-template `trail_enabled_by_default` escape hatch is
 * preserved and now behaves as a genuine OPT-IN — see the trails initialiser in
 * EnhancedEvaluationDisplay.
 *
 * NOTE: `pose` and `ghost` are mutually exclusive at the control layer (they
 * share one canvas), so `ghost: true` here would silently suppress Pose.
 */
export const DEFAULT_OVERLAY_STATE: EvalOverlayState = {
  pose: true,
  ghost: false,
  trails: false,
  goals: false,
  regions: false,
  viewMode: '2d',
};

/** Map EvalOverlayState.ghost → VisualizationMode.
 *  Ghost ON  → 'ghost_echo'
 *  Ghost OFF → 'off'  (pose remains enabled; 'off' mode disables UnifiedPoseOverlay)
 *  Note: 'off' in vizConfig means NO pose overlay at all. When ghost is off but
 *  pose is on, the caller should keep a previously-selected non-ghost mode or
 *  fall back to 'ghost_echo'. The simple toggle behaviour here is:
 *    ghost ON  → mode = 'ghost_echo'
 *    ghost OFF → mode = 'off'  (UnifiedPoseOverlay not rendered)
 *  This matches RF#1: "Ghost pill must map to setMode('ghost_echo') / setMode('off')."
 */
export function ghostToVizMode(ghost: boolean): VisualizationMode {
  return ghost ? 'ghost_echo' : 'off';
}

/**
 * Resolve the ONE exclusive canvas mode from the Pose + Ghost pill bits.
 *
 *   Ghost ON             → 'ghost_echo'            (translucent template)
 *   Pose ON  + Ghost OFF → 'differential_skeleton' (athlete skeleton)
 *   both OFF             → 'off'                   (clean video)
 *
 * Pose and Ghost share a single canvas, so exactly one mode can be live. The
 * ghost-first ordering is a defensive tiebreak only: `togglePose`/`toggleGhost`
 * in EnhancedEvaluationDisplay keep the two mutually exclusive, so the
 * both-true case is unreachable from the UI (owner ruling 2026-08-03).
 *
 * Single source of truth for BOTH the initial useVisualizationMode seed and the
 * sync effect. Seeding from `ghostToVizMode` alone used to be correct only
 * because the default was `ghost: true`; once the default became Pose-only the
 * seed ('off') disagreed with what the effect immediately set
 * ('differential_skeleton'). Deriving both from this function removes that
 * second source of truth.
 */
export function overlayBitsToVizMode(pose: boolean, ghost: boolean): VisualizationMode {
  if (ghost) return 'ghost_echo';
  if (!pose) return 'off';
  return 'differential_skeleton';
}

// ---------------------------------------------------------------------------
// Pose / Ghost toggle reducers — MUTUALLY EXCLUSIVE (owner ruling 2026-08-03)
//
// "Pose+Ghost together showing both sounds very confusing. Let's have them do
// their separate things."
//
// The two overlays share one canvas driven by a single exclusive vizConfig.mode,
// so both-on could never composite: the ghost won and Pose went silent, which
// read as a broken pill. These reducers make the CONTROL layer enforce the
// exclusivity the RENDER layer already had. Both-off remains valid (clean video).
//
// Exported as pure reducers (rather than inlined in EnhancedEvaluationDisplay)
// so the unit tests exercise the REAL implementation — a mirrored copy in the
// test file would keep passing while the component regressed. Pass straight to
// setState: `setOverlayState(togglePoseBit)`.
// ---------------------------------------------------------------------------

/** Toggle Pose. Turning it ON forces Ghost off. */
export function togglePoseBit(s: EvalOverlayState): EvalOverlayState {
  return s.pose ? { ...s, pose: false } : { ...s, pose: true, ghost: false };
}

/** Toggle Ghost. Turning it ON forces Pose off. */
export function toggleGhostBit(s: EvalOverlayState): EvalOverlayState {
  return s.ghost ? { ...s, ghost: false } : { ...s, ghost: true, pose: false };
}
